import { z } from 'zod';
import { CnyAmount } from './money.js';

// Additive domain contract; this does not mount or version any HTTP endpoint.
export const PUBLIC_CATALOG_CONTRACT_VERSION = '0.4.0-catalog-r1' as const;
export const PUBLIC_CATALOG_LIMITS = {
  defaultPageSize:30, maxPageSize:100, maxSourceBytes:8*1024*1024,
  maxResponseBytes:2*1024*1024, timeoutMs:8000, maxAttempts:1,
  maxModels:10000, maxGroups:256, maxVendors:256,
  maxPlansPerGroup:64, maxTiersPerPlan:256, maxLinesPerTier:128,
} as const;
const Id = z.string().min(1).max(256).refine(value=>value.trim()===value && !/[\u0000-\u001f\u007f]/.test(value),'INVALID_PUBLIC_ID');
const GroupId = Id.refine(value=>value!=='auto'&&value!=='all','GROUP_SENTINEL_NOT_PUBLIC');
const Digest = z.string().regex(/^sha256:[a-f0-9]{64}$/);
const Text = z.string().min(1).max(512);
const Category = z.enum(['chat','image','video','audio','embedding','rerank','other']);
const unique = (values:readonly string[])=>new Set(values).size===values.length;

export const PublicCatalogQuery = z.object({
  search:z.string().max(200).optional(), category:Category.optional(),
  vendorId:Id.optional(), groupId:GroupId.optional(),
  sort:z.enum(['model_asc','model_desc']).default('model_asc'),
  limit:z.number().int().min(1).max(PUBLIC_CATALOG_LIMITS.maxPageSize).default(PUBLIC_CATALOG_LIMITS.defaultPageSize),
  cursor:z.string().min(1).max(2048).optional(),
}).strict();

export const PublicCatalogVendor = z.object({id:Id,name:Text}).strict();
// Group multipliers are applied on the server. No ratio is sent for client arithmetic.
export const PublicCatalogGroup = z.object({id:GroupId,name:Text}).strict();
export const PublicPriceDenominator = z.object({
  quantity:z.string().regex(/^[1-9]\d{0,17}$/),
  unit:z.enum(['request','second','token','image','character','credit','count']),
  label:Text,
}).strict();
export const PublicPriceLine = z.object({
  id:Id,label:Text,
  component:z.enum(['request','input','output','cache_read','cache_write','cache_write_1h','cache_write_12h','image_input','image_cache_read','image_output','audio_input','audio_output','usage']),
  usageField:z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/).nullable(),
  currency:z.literal('CNY'),amount:CnyAmount,denominator:PublicPriceDenominator,
}).strict();
export const PublicPriceTier = z.object({
  id:Id,label:Text,
  condition:z.discriminatedUnion('kind',[
    z.object({kind:z.literal('always')}).strict(),
    z.object({kind:z.enum(['conditional','fallback']),text:Text}).strict(),
  ]),
  // Ordered source rules and every priced component, including explicit zero, survive.
  lines:z.array(PublicPriceLine).min(1).max(PUBLIC_CATALOG_LIMITS.maxLinesPerTier),
}).strict().refine(tier=>unique(tier.lines.map(line=>line.id)),'DUPLICATE_PRICE_LINE');

const planIdentity = {id:Id,label:Text,sourceRuleDigest:Digest.nullable()};
export const PublicCatalogPricePlan = z.discriminatedUnion('status',[
  z.object({
    ...planIdentity,status:z.literal('known'),mode:z.enum(['request','token','tiered','usage']),
    coverage:z.literal('complete'),groupApplied:z.literal(true),
    tiers:z.array(PublicPriceTier).min(1).max(PUBLIC_CATALOG_LIMITS.maxTiersPerPlan),
  }).strict().refine(plan=>unique(plan.tiers.map(tier=>tier.id)),'DUPLICATE_PRICE_TIER'),
  z.object({
    ...planIdentity,status:z.literal('unknown'),
    reason:z.enum(['missing_price','unsupported_expression','incomplete_unit','incomplete_group_price','unrepresentable_amount','projection_limit','not_verified']),
    // A partly understood rule is not a complete table or a zero price.
    coverage:z.literal('unknown'),tiers:z.null(),
  }).strict().refine(plan=>plan.reason!=='unsupported_expression'||plan.sourceRuleDigest!==null,'UNSUPPORTED_RULE_DIGEST_REQUIRED'),
]);
const GroupPrices = z.object({
  groupId:GroupId,plans:z.array(PublicCatalogPricePlan).min(1).max(PUBLIC_CATALOG_LIMITS.maxPlansPerGroup),
}).strict().refine(value=>unique(value.plans.map(plan=>plan.id)),'DUPLICATE_PRICE_PLAN');

export const PublicCatalogModel = z.object({
  // Public callable alias, never an upstream model/channel/plugin identifier.
  id:Id,name:Text,category:Category,vendorId:Id.nullable(),
  description:z.string().max(8192),tags:z.array(z.string().min(1).max(64)).max(32),
  groups:z.array(GroupId).min(1).max(PUBLIC_CATALOG_LIMITS.maxGroups),
  endpoints:z.array(z.object({
    type:z.string().min(1).max(80),method:z.enum(['GET','POST']),
    // Approved relative API documentation paths only; never a provider host.
    path:z.string().max(256).regex(/^\/v1\/[A-Za-z0-9_/-]+$/),
  }).strict()).max(32),
  specifications:z.array(z.object({key:Id,label:Text,value:Text,unit:Text.nullable()}).strict()).max(32),
  specificationCoverage:z.enum(['complete','unknown']),
  executionStatus:z.literal('not_verified'),
  pricesByGroup:z.array(GroupPrices).min(1).max(PUBLIC_CATALOG_LIMITS.maxGroups),
}).strict().superRefine((model,ctx)=>{
  const groups=model.groups,prices=model.pricesByGroup.map(value=>value.groupId);
  if(!unique(groups)||!unique(prices)||groups.length!==prices.length||groups.some(id=>!prices.includes(id))) {
    ctx.addIssue({code:'custom',message:'PUBLIC_GROUP_PRICE_COVERAGE_REQUIRED'});
  }
  if(!unique(model.specifications.map(value=>value.key)))ctx.addIssue({code:'custom',message:'DUPLICATE_SPECIFICATION'});
});

export const PublicCatalogSnapshot = z.object({
  sourceId:Id,sourceRevision:z.string().min(1).max(256),
  sitePublicationRevision:Id,visibilityRevision:Digest,
  contentVersion:Digest,pricingVersion:Digest,fetchedAt:z.string().datetime(),
  currency:z.literal('CNY'),rateBasis:z.literal('quota_500000_per_cny'),
}).strict();
const snapshotFields = {
  contractVersion:z.literal(PUBLIC_CATALOG_CONTRACT_VERSION),snapshot:PublicCatalogSnapshot,
  categories:z.array(Category).max(7),
  vendors:z.array(PublicCatalogVendor).max(PUBLIC_CATALOG_LIMITS.maxVendors),
  groups:z.array(PublicCatalogGroup).max(PUBLIC_CATALOG_LIMITS.maxGroups),
};
// Success data only. Dependency/auth/limit/cursor failures use the existing HTTP
// ErrorEnvelope, not an empty catalog, an unknown price, or stale success data.
export const PublicCatalogPage = z.discriminatedUnion('status',[
  z.object({
    ...snapshotFields,status:z.literal('ready'),
    items:z.array(PublicCatalogModel).min(1).max(PUBLIC_CATALOG_LIMITS.maxPageSize),
    total:z.number().int().min(1).max(PUBLIC_CATALOG_LIMITS.maxModels),
    nextCursor:z.string().min(1).max(2048).nullable(),
  }).strict(),
  z.object({
    ...snapshotFields,status:z.literal('empty'),items:z.array(PublicCatalogModel).max(0),
    total:z.literal(0),nextCursor:z.null(),
  }).strict(),
]).superRefine((page,ctx)=>{
  if(!unique(page.categories)||!unique(page.vendors.map(v=>v.id))||!unique(page.groups.map(g=>g.id))||!unique(page.items.map(m=>m.id))) {
    ctx.addIssue({code:'custom',message:'DUPLICATE_PUBLIC_CATALOG_ID'});
  }
  if(page.total<page.items.length||page.nextCursor!==null&&page.total<=page.items.length)ctx.addIssue({code:'custom',message:'INVALID_PUBLIC_CATALOG_PAGE'});
  const vendors=new Set(page.vendors.map(v=>v.id)),groups=new Set(page.groups.map(g=>g.id));
  for(const model of page.items)if(!page.categories.includes(model.category)||model.vendorId!==null&&!vendors.has(model.vendorId)||model.groups.some(id=>!groups.has(id))) {
    ctx.addIssue({code:'custom',message:'UNPUBLISHED_CATALOG_REFERENCE'});
  }
});

export type PublicCatalogQueryData=z.infer<typeof PublicCatalogQuery>;
export type PublicCatalogPageData=z.infer<typeof PublicCatalogPage>;
export type PublicCatalogModelData=z.infer<typeof PublicCatalogModel>;
export type PublicCatalogPricePlanData=z.infer<typeof PublicCatalogPricePlan>;
